import React from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import NetworkDisconnectionComponent from './NetworkDisconnectionComponent/NetworkDisconnectionComponent';
import { sendToSentry } from '../libs/sentryErrorLogging';
import IntlProviderDefaultMessages from './IntlProviderDefaultMessages';
import omit from 'lodash/omit';
import { ErrorWithHTTPStatusCode, isInternalError } from '../libs/errors';

const supportedLanguages = ['en', 'nl', 'fr'];

/**
 * Wrapper component that listens to _online_, _offline_ and _unhandledrejection_ browser events.
 * When offline, the component renders the <a href="/docs/pages-networkdisconnectioncomponent--with-retry">NetworkDisconnection</a> component.
 * Depending on the error code the error is sent to Sentry when running in production mode.
 *
 * Depending on the type of event a Retry button is shown; this is the case for _unhandledrejections_s but not _offline_ events.
 *
 * When online, its `children` are rendered as is.
 *
 * It also detects when a request receives a 440 reply and properly logs out the user, causing a redirect to the login page
 */
export default class NetworkDisconnectionBoundary extends React.Component {
  constructor(props) {
    super(props);
    // Use the language of the browser if we do not know the locale of the App yet, reverting to English as a default
    const browserLocale = navigator.language || navigator.userLanguage;
    const browserLanguage = browserLocale ? browserLocale.slice(0, 2) : null;
    const defaultLanguage =
      browserLanguage && supportedLanguages.includes(browserLanguage)
        ? browserLanguage
        : 'en';
    this.state = {
      disconnected: false,
      canRetry: false,
      locale: defaultLanguage,
      id: uuid()
    };
    this.handleConnectionChange = this.handleConnectionChange.bind(this);
    this.handleUnhandledRejection = this.handleUnhandledRejection.bind(this);
    this.retryConnection = this.retryConnection.bind(this);
    this.setLocale = this.setLocale.bind(this);
  }

  componentDidMount() {
    this.handleConnectionChange();
    window.addEventListener('online', this.handleConnectionChange);
    window.addEventListener('offline', this.handleConnectionChange);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('online', this.handleConnectionChange);
    window.removeEventListener('offline', this.handleConnectionChange);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  setLocale(locale) {
    this.setState({ locale });
  }

  handleConnectionChange() {
    if (this.state.disconnected !== !navigator.onLine)
      this.setState({ disconnected: !navigator.onLine, canRetry: false });
  }

  handleUnhandledRejection(event) {
    if (!event || !event.reason) return;

    if (!event.reason.handledByBoundary) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (event.reason instanceof Error && isInternalError(event.reason)) {
      if (event.reason.name === 'SessionExpiredError')
        this.yesplanAppFrameRef.current.logoutOfApp();
      return;
    }

    let eventCode = null;
    if (event.reason instanceof ErrorWithHTTPStatusCode) eventCode = event.reason.code;
    else if (event.reason.method) eventCode = 503;

    if (eventCode >= 500 && eventCode < 600)
      this.setState({ disconnected: true, canRetry: true });
    else sendToSentry(event);
  }

  retryConnection() {
    this.setState({ disconnected: false, canRetry: false });
  }

  render() {
    const { children } = this.props;
    this.yesplanAppFrameRef = React.createRef();
    const propsForChildren = {
      ...omit(this.props, ['children']),
      setLocale: this.setLocale,
      innerRef: this.yesplanAppFrameRef
    };

    if (this.state.disconnected) {
      return (
        <IntlProviderDefaultMessages locale={this.state.locale}>
          <>
            {!this.state.canRetry && (
              <div id={this.state.id}>{children(propsForChildren)}</div>
            )}
            <NetworkDisconnectionComponent
              canRetry={this.state.canRetry}
              retryConnection={this.retryConnection}
            />
          </>
        </IntlProviderDefaultMessages>
      );
    }
    return (
      <IntlProviderDefaultMessages locale={this.state.locale}>
        <div id={this.state.id}>{children(propsForChildren)}</div>
      </IntlProviderDefaultMessages>
    );
  }
}

NetworkDisconnectionBoundary.propTypes = {
  children: PropTypes.func.isRequired
};

NetworkDisconnectionBoundary.defaultProps = {};
